import { useState, useEffect, useCallback } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export interface UserProfile {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  plan: string;
  status: string;
  role: string;
  member_grade: string | null;
  credit_balance: number;
  is_email_verified: boolean;
  created_at: string;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
}

// ─── 모듈 레벨 싱글톤 ────────────────────────────────────────────────────────

/**
 * 새로고침 시 React 첫 렌더부터 isLoggedIn=true가 되도록 localStorage에서
 * 세션을 동기적으로 복원. 이렇게 안 하면 INITIAL_SESSION 비동기 발화 사이에
 * `cachedState.user=null`이라 보호 페이지가 리다이렉트하거나 AuthModal이 뜸.
 *
 * 보안: 클라이언트 측 user 객체는 UI 표시용. 실제 권한은 access_token으로
 * 서버 호출 시 검증되므로 위조된 user 객체로는 데이터 접근 불가.
 */
function readStoredSession(): { user: User; session: Session } | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem('sb-session');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      access_token?: string;
      refresh_token?: string;
      user?: User;
      expires_at?: number;
    };
    if (!parsed?.access_token || !parsed?.refresh_token || !parsed?.user) return null;
    return { user: parsed.user, session: parsed as unknown as Session };
  } catch {
    return null;
  }
}

/**
 * profile도 localStorage 캐시에서 동기 복원 — 새로고침 시 크레딧 잔액 등이
 * 비동기 fetchProfile 동안 placeholder로 보이지 않도록.
 * user.id 일치 확인으로 다른 사용자 데이터 노출 방지.
 */
const PROFILE_STORAGE_KEY = 'wm-profile';
function readStoredProfile(expectedUserId: string): UserProfile | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(PROFILE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UserProfile;
    if (parsed?.id !== expectedUserId) return null;
    return parsed;
  } catch {
    return null;
  }
}
function writeStoredProfile(profile: UserProfile | null) {
  if (typeof window === 'undefined') return;
  try {
    if (profile) {
      window.localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile));
    } else {
      window.localStorage.removeItem(PROFILE_STORAGE_KEY);
    }
  } catch { /* 무시 */ }
}

const initialStored = readStoredSession();
const initialProfile = initialStored ? readStoredProfile(initialStored.user.id) : null;
let cachedState: AuthState = {
  user: initialStored?.user ?? null,
  session: initialStored?.session ?? null,
  profile: initialProfile,
  loading: true,
};
let initPromise: Promise<void> | null = null;
const listeners = new Set<(state: AuthState) => void>();

// user ID alone is not enough: A -> logout -> A is a different login generation.
let authGeneration = 0;
let profileRequestSerial = 0;
let initialRefreshTimer: ReturnType<typeof setTimeout> | null = null;

function invalidateAuthWork() {
  authGeneration++;
  profileRequestSerial++;
  if (initialRefreshTimer !== null) {
    clearTimeout(initialRefreshTimer);
    initialRefreshTimer = null;
  }
}

function isCurrentProfileRequest(userId: string, generation: number, request: number) {
  return cachedState.user?.id === userId
    && authGeneration === generation
    && profileRequestSerial === request;
}

function notifyListeners(next: AuthState) {
  // Never display or retain a profile belonging to a previous account.
  const profile = next.user && next.profile?.id === next.user.id ? next.profile : null;
  const state = { ...next, profile };
  cachedState = state;
  writeStoredProfile(profile);
  listeners.forEach((fn) => fn(state));
}

// null means a successful empty read; undefined means unavailable/failed.
// Only a confirmed empty read may authorize the existing signup fallback.
async function fetchProfile(userId: string): Promise<UserProfile | null | undefined> {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, email, display_name, avatar_url, plan, status, role, member_grade, credit_balance, is_email_verified, created_at')
      .eq('id', userId)
      .maybeSingle();
    if (error) return undefined;
    return data as UserProfile | null | undefined;
  } catch {
    return undefined;
  }
}

async function createProfile(user: User): Promise<UserProfile | null> {
  try {
    const displayName =
      user.user_metadata?.full_name ||
      user.user_metadata?.name ||
      user.email?.split('@')[0] ||
      '사용자';
    const { data, error } = await supabase
      .from('user_profiles')
      // 권한 컬럼(plan·status·role·credit_balance·is_email_verified)은 보내지 않는다.
      //
      // 이 경로는 폴백이다. 정상 가입은 auth.users 의 on_auth_user_created 트리거가
      // handle_new_user() 로 프로필을 먼저 만든다(plan=free, status=active, role=user,
      // credit_balance=0). 여기서 같은 값을 다시 보내도 PK 충돌로 버려질 뿐이었다.
      //
      // 그리고 2026-09-22 마이그레이션으로 클라이언트의 해당 컬럼 쓰기 권한을 회수했다.
      // 계속 보내면 권한 오류로 폴백 자체가 실패하므로, 나머지는 DB 기본값에 맡긴다.
      .insert({
        id: user.id,
        email: user.email ?? '',
        display_name: displayName,
        avatar_url: user.user_metadata?.avatar_url ?? null,
        signup_source: 'web',
        language: 'ko',
      })
      .select('id, email, display_name, avatar_url, plan, status, role, member_grade, credit_balance, is_email_verified, created_at')
      .maybeSingle();
    if (error) return null;
    return data as UserProfile | null;
  } catch {
    return null;
  }
}

async function loadCurrentProfile(
  user: User,
  allowCreate: boolean,
  generation: number,
  request: number,
): Promise<void> {
  if (!isCurrentProfileRequest(user.id, generation, request)) return;
  let profile = await fetchProfile(user.id);
  // A stale read must not trigger a fallback INSERT under the next session either.
  if (!isCurrentProfileRequest(user.id, generation, request)) return;
  if (profile === null && allowCreate) profile = await createProfile(user);
  if (profile?.id === user.id && isCurrentProfileRequest(user.id, generation, request)) {
    // Use the current session, not the token captured before the async request.
    notifyListeners({ ...cachedState, profile, loading: false });
  }
}

// 크레딧 잔액을 싱글톤 캐시에 즉시 반영 (Navbar 실시간 업데이트)
export function updateCachedCreditBalance(newBalance: number) {
  if (!cachedState.profile) return;
  notifyListeners({
    ...cachedState,
    profile: { ...cachedState.profile, credit_balance: newBalance },
  });
}

function initAuthSingleton() {
  if (initPromise) return;

  let resolveInit!: () => void;
  let initResolved = false;
  initPromise = new Promise<void>((r) => { resolveInit = r; });
  const safeResolveInit = () => {
    if (!initResolved) { initResolved = true; resolveInit(); }
  };

  if (typeof window !== 'undefined') {
    const existingSubscription = (window as unknown as Record<string, unknown>).__wowmeta_auth_subscription;
    if (!existingSubscription) {
      // ⚠️ 이 콜백 안에서 Supabase 질의를 await 하면 안 된다.
      //
      // supabase-js 는 이 콜백을 **인증 초기화가 끝나기 전에** 부른다
      // (_recoverAndRefresh → _notifyAllSubscribers). 그 사이 initializePromise 는
      // 아직 pending 이고, 모든 supabase 질의는 첫 줄에서 그 promise 를 기다린다.
      // 그래서 여기서 await 하면 서로를 붙잡고 **인증이 통째로 멈춘다** —
      // 네트워크 요청조차 나가지 않고, 화면은 로딩에서 영원히 머문다.
      //
      // 실제로 그랬다. 캐시된 프로필이 있는 재방문자는 아래 첫 분기(await 없음)로
      // 빠져 멀쩡했고, **캐시가 없는 첫 방문·첫 로그인만** 멈췄다. 그래서 오래
      // 눈에 띄지 않았다. E2E 에서 로그인 상태를 만들면 항상 이 경로라 100% 재현된다.
      //
      // 규칙: 콜백은 동기적으로 끝낸다. 이어지는 조회는 콜백 밖으로 던진다.
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        invalidateAuthWork();
        if (session?.user) {
          const user = session.user;
          const profile = cachedState.profile?.id === user.id ? cachedState.profile : null;
          // Login is immediate; a different account never inherits the old profile.
          const generation = authGeneration;
          const request = ++profileRequestSerial;
          notifyListeners({ user, session, profile, loading: false });
          const allowCreate = !profile && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION');
          // Keep all Supabase queries outside the synchronous auth callback/SDK lock.
          setTimeout(() => {
            void loadCurrentProfile(user, allowCreate, generation, request).catch(() => {});
          }, 0);
        } else {
          // INITIAL_SESSION(null) = cold start로 인한 일시 실패 가능성.
          // cachedState.user(localStorage 동기 복원분)가 있으면 user 유지 + 백그라운드 refresh.
          // SIGNED_OUT 또는 명시적 null 세션만 진짜 로그아웃으로 처리.
          if (event === 'INITIAL_SESSION' && cachedState.user) {
            safeResolveInit();
            const generation = authGeneration;
            const userId = cachedState.user.id;
            initialRefreshTimer = setTimeout(() => {
              initialRefreshTimer = null;
              if (generation !== authGeneration || cachedState.user?.id !== userId) return;
              void supabase.auth.refreshSession().catch(() => {});
            }, 1500);
            notifyListeners({ ...cachedState, loading: false });
            return;
          }
          notifyListeners({ user: null, session: null, profile: null, loading: false });
        }

        if (event === 'INITIAL_SESSION') safeResolveInit();
      });
      (window as unknown as Record<string, unknown>).__wowmeta_auth_subscription = subscription;
    } else {
      safeResolveInit();
    }
  } else {
    safeResolveInit();
  }
}

initAuthSingleton();
// ─────────────────────────────────────────────────────────────────────────────

export function useAuth() {
  const [state, setState] = useState<AuthState>(cachedState);

  useEffect(() => {
    setState(cachedState);
    const listener = (s: AuthState) => setState(s);
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
        // App logout does not clear Google's session; allow an explicit account switch.
        queryParams: { prompt: 'select_account' },
      },
    });
    return { data, error };
  }, []);

  const signOut = useCallback(async () => {
    // ⚠️ 핵심: SDK signOut을 await 하지 않음.
    // SDK는 서버에 admin.signOut 네트워크 호출 후에야 _removeSession()을 실행.
    // cold start로 60초 hang 시 사용자는 "로그아웃 안 됨"으로 인식 + 새로고침 시
    // localStorage가 아직 비어있지 않아 다시 로그인 상태로 복원되는 버그 발생.
    //
    // 해결: localStorage·캐시 즉시 정리 → UI 즉시 반영 → 서버 signOut은 fire-and-forget.

    // Invalidate deferred queries before publishing logout or touching the SDK.
    invalidateAuthWork();

    // 1) UI 즉시 로그아웃 상태로 업데이트
    notifyListeners({ user: null, session: null, profile: null, loading: false });

    // 2) localStorage·sessionStorage 즉시 정리
    try { localStorage.removeItem('sb-session'); } catch { /* 무시 */ }
    try { localStorage.removeItem('sb-session-user'); } catch { /* 무시 */ }
    try { localStorage.removeItem('sb-session-code-verifier'); } catch { /* 무시 */ }
    try { localStorage.removeItem(PROFILE_STORAGE_KEY); } catch { /* 무시 */ }
    try { sessionStorage.removeItem('admin_guard_running'); } catch { /* 무시 */ }

    // 3) 서버 세션 무효화는 백그라운드에서 (실패해도 무시 — 로컬은 이미 정리됨)
    supabase.auth.signOut().catch(() => {});
  }, []);

  // DB에서 최신 프로필 다시 불러와서 싱글톤 캐시 갱신
  const refreshProfile = useCallback(async () => {
    const user = cachedState.user;
    if (!user) return;
    await loadCurrentProfile(user, false, authGeneration, ++profileRequestSerial);
  }, []);

  return {
    user: state.user,
    session: state.session,
    profile: state.profile,
    loading: state.loading,
    isLoggedIn: !!state.user,
    signInWithGoogle,
    signOut,
    refreshProfile,
  };
}
